import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

type AvailabilityDomain = { id: string; name: string };

export function OciAvailabilityDomainPickerComponent({
  onChange,
  rawErrors,
  formData,
  formContext,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const [domains, setDomains] = useState<AvailabilityDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!region || !compartmentId) {
      setDomains([]);
      onChange(undefined as any);
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/availability-domains?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(compartmentId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load availability domains',
            );
          } else {
            const list: AvailabilityDomain[] = data.availabilityDomains ?? [];
            setDomains(list);
            if (list.length > 0) onChange(list[0].name);
          }
        })
        .catch(() => setError('Failed to load availability domains'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId]);

  if (loading) return <CircularProgress size={20} />;

  return (
    <FormControl fullWidth error={!!rawErrors?.length || !!error}>
      <InputLabel>Availability Domain</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={!region || !compartmentId}
      >
        {(!region || !compartmentId) && (
          <MenuItem value="" disabled>
            Select region and compartment first
          </MenuItem>
        )}
        {domains.map(d => (
          <MenuItem key={d.id} value={d.name}>
            {d.name}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

export const OciAvailabilityDomainPickerField = createFormField({
  name: 'OciAvailabilityDomainPicker',
  component: OciAvailabilityDomainPickerComponent,
});
